/**
 * Navbar component that renders a navigation bar with multiple items and sub-items.
 * It uses the `useTranslation` hook from `react-i18next` for internationalization.
 *
 * @remarks
 * The component includes a logo, navigation items with dropdowns, and action buttons.
 */
import React, { useEffect, useState } from 'react';
import './Navbar.scss';
import { useTranslation } from 'react-i18next';
import { useCustomNavigation } from '../../utility/Utility.ts';
import { navbarElements } from './data.ts';
import NavbarItemComponent from './NavbarItemComponent.tsx';
import NavbarMenuComponent from './NavbarMenuComponent.tsx';

const Navbar = () => {
    const {t} = useTranslation();
    const customNavigate = useCustomNavigation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);

    const handleNavigation = (isRouterLink: boolean, link: string) => {
        customNavigate(isRouterLink, link);
    };

    // Update 'isSmallScreen' state when the screen size changes
    useEffect(() => {
        const handleResize = () => {
        setIsSmallScreen(window.innerWidth <= 1024); // Toggle isSmallScreen based on screen width
        if (window.innerWidth > 1024) {
            setIsMenuOpen(false); // Close the menu when resizing to larger screens
        }
        };

        // Event listener for window resize
        window.addEventListener('resize', handleResize);

        // Clean up the event listener when the component unmounts
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <nav id="navbar" className="bg-blue-0F1">
            <>{
                isSmallScreen ?
                (
                    <div id="navbar-container-1" className="py-6 px-5">
                        <img loading="lazy" src="/assets/images/svg/logo.svg" alt="innovii" className="w-24 cursor-pointer" onClick={() => handleNavigation(true, '')} />
                        <img loading="lazy" src="/assets/images/svg/hamburger-menu-icon.svg" alt="=" className="w-6 cursor-pointer" onClick={() => setIsMenuOpen(true)} />
                    </div>
                ) : (
                    <div id="navbar-container-2" className="px-5">
                        <div className="flex gap-8">
                            <img loading="lazy" src="/assets/images/svg/logo.svg" alt="innovii" className="cursor-pointer" onClick={() => handleNavigation(true, '')} />
                            <div className="flex gap-8">
                                {navbarElements.map((element, index: number) => (
                                    <React.Fragment key={index}>
                                        <NavbarItemComponent
                                            index={index}
                                            element={element}
                                        />
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-3 items-center">
                            <button className="btn btn-trans-white" onClick={() => handleNavigation(true, 'services/digital-transformation')}>{t('NAVBAR.LEARN_MORE')}</button>
                            <button className="btn btn-white-blue" onClick={() => handleNavigation(true, 'contact-us')}>{t('NAVBAR.CONTACT_US')}</button>
                        </div>
                    </div>
                )
            }</>
            <>
                {
                    (isMenuOpen) &&
                    (<NavbarMenuComponent handleCloseMenu={() => setIsMenuOpen(false)} />)
                }
            </>
        </nav>
    );
};

export default Navbar;